import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldAlert } from "lucide-react";

// Show ONCE per browser day. Tracks per-user in localStorage so a different
// person logging in on the same machine still sees their own modal.
function dayKeyFor(userId) {
  return `hr_warn_ack_${userId}_${new Date().toISOString().slice(0, 10)}`;
}

export default function WarningsModal() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    if (!user.terms_accepted) return; // Terms first
    if (!user.profile_completed) return; // KYC before warnings modal
    const summary = user.warnings_summary;
    if (!summary || !summary.unacknowledged_count) return;
    const key = dayKeyFor(user.id);
    if (localStorage.getItem(key)) return; // already shown today
    (async () => {
      const { data } = await api.get("/me/warnings");
      const unack = (data.active || []).filter((w) => !w.acknowledged_at);
      if (unack.length > 0) {
        setWarnings(unack);
        setOpen(true);
      }
    })();
  }, [user]);

  const acknowledgeAll = async () => {
    setBusy(true);
    try {
      await Promise.all(warnings.map((w) => api.post(`/warnings/${w.id}/acknowledge`)));
      localStorage.setItem(dayKeyFor(user.id), "1");
      // Refresh user so the unack count updates everywhere
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const hardCount = warnings.filter((w) => w.severity === "hard").length;
  const headerColor = hardCount > 0 ? "text-red-600" : "text-orange-600";
  const Icon = hardCount > 0 ? ShieldAlert : AlertTriangle;

  return (
    <Dialog open={open} onOpenChange={() => { /* require ack — no soft-close */ }}>
      <DialogContent className="max-w-lg" data-testid="warnings-modal" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} hideClose>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${headerColor}`}>
            <Icon className="w-5 h-5" />
            Important: You have {warnings.length} new {warnings.length === 1 ? "warning" : "warnings"}
          </DialogTitle>
          <DialogDescription>
            Please read and acknowledge before continuing to work.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto" data-testid="warnings-modal-list">
          {warnings.map((w) => (
            <div
              key={w.id}
              className={`border-l-4 rounded-md p-3 ${w.severity === "hard" ? "border-red-500 bg-red-50" : "border-orange-500 bg-orange-50"}`}
              data-testid={`warning-card-${w.id}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[10px] uppercase tracking-widest font-bold ${w.severity === "hard" ? "text-red-700" : "text-orange-700"}`}>
                  {w.severity === "hard" ? "Hard Warning" : "Soft Warning"}
                </span>
                <span className="text-[11px] text-zinc-500">{new Date(w.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-1 text-sm font-medium text-zinc-900">{w.reason}</div>
              <div className="mt-1 text-[11px] text-zinc-500">Issued by {w.issued_by_name}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            onClick={acknowledgeAll}
            disabled={busy}
            className="bg-zinc-950 hover:bg-zinc-800"
            data-testid="warnings-acknowledge"
          >
            I have read and understood
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
